// features/products/pages/ProductBarcodesPage.tsx
/**
 * Product Barcodes Page - Standalone Barcode Management
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getProduct, ProductData, ProductBarcodeData,
} from '../services/productsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Plus, Trash2, Barcode, Star, CheckCircle,
    Printer, Copy,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

const BARCODE_TYPES = ['EAN-13', 'UPC-A', 'CODE-128', 'QR', 'EAN-8', 'ITF-14', 'CODE-39'];

export function ProductBarcodesPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showAddModal, setShowAddModal] = useState(false);
    const [newBarcode, setNewBarcode] = useState('');
    const [newType, setNewType] = useState('EAN-13');
    const [newIsPrimary, setNewIsPrimary] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    const { data: product, isLoading } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const addMutation = useMutation({
        mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/products/${productId}/barcodes`, {
            barcode: newBarcode,
            barcodeType: newType,
            isPrimary: newIsPrimary,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            setShowAddModal(false);
            setNewBarcode('');
            setNewType('EAN-13');
            setNewIsPrimary(false);
            addToast({ type: 'success', title: 'Barcode added' });
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const setPrimaryMutation = useMutation({
        mutationFn: (barcodeId: string) =>
            apiClient.post(`/tenants/${user!.tenantId}/products/${productId}/barcodes/${barcodeId}/primary`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            addToast({ type: 'success', title: 'Primary barcode set' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (barcodeId: string) =>
            apiClient.delete(`/tenants/${user!.tenantId}/products/barcodes/${barcodeId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product', productId] });
            setDeleteConfirm(null);
            addToast({ type: 'success', title: 'Barcode deleted' });
        },
    });

    const barcodes = product?.barcodes || [];

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        addToast({ type: 'success', title: 'Copied to clipboard' });
    };

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (!product) return <ErrorState title="Product not found" />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate(`/products/${productId}`)} className="p-2 rounded-xl hover:bg-slate-100" type="button"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
                            <div className="p-2.5 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl shadow-lg"><Barcode className="w-5 h-5 text-white" /></div>
                            <div><h1 className="text-xl font-bold text-slate-900">{product.name}</h1><p className="text-sm text-slate-500">{barcodes.length} barcode{barcodes.length !== 1 ? 's' : ''}</p></div>
                        </div>
                        <button onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg text-sm" type="button">
                            <Plus className="w-4 h-4" /> Add Barcode
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1200px] mx-auto p-6 lg:p-8">
                {barcodes.length === 0 ? (
                    <EmptyState icon={<Barcode className="w-16 h-16" />} title="No Barcodes" description="Add barcodes for scanning at POS." action={{ label: 'Add Barcode', onClick: () => setShowAddModal(true) }} />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead><tr className="bg-slate-50/50">
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Barcode</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Primary</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                                </tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                    {barcodes.map((bc) => (
                                        <tr key={bc.id} className="group hover:bg-slate-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <Barcode className="w-5 h-5 text-slate-400" />
                                                    <span className="text-sm font-mono font-bold text-slate-800">{bc.barcode}</span>
                                                    <button onClick={() => copyToClipboard(bc.barcode)} className="p-1 rounded text-slate-300 hover:text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" type="button"><Copy className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4"><Badge variant="neutral" size="sm">{bc.barcodeType}</Badge></td>
                                            <td className="px-6 py-4 text-center">
                                                {bc.isPrimary ? <Star className="w-5 h-5 text-amber-500 fill-amber-500 mx-auto" /> : (
                                                    <button onClick={() => setPrimaryMutation.mutate(bc.id)} className="text-slate-300 hover:text-amber-500 transition-colors" type="button" title="Set as primary"><Star className="w-5 h-5" /></button>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button onClick={() => setDeleteConfirm(bc.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all" type="button"><Trash2 className="w-4 h-4" /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>

            {/* Add Barcode Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
                            <h2 className="text-lg font-bold mb-4">Add Barcode</h2>
                            <div className="space-y-4">
                                <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Barcode *</label><input type="text" value={newBarcode} onChange={e => setNewBarcode(e.target.value)} className="w-full px-3 py-2.5 border rounded-xl text-sm font-mono" placeholder="Scan or enter barcode" autoFocus /></div>
                                <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Type</label>
                                    <select value={newType} onChange={e => setNewType(e.target.value)} className="w-full px-3 py-2.5 border rounded-xl text-sm bg-white">
                                        {BARCODE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={newIsPrimary} onChange={e => setNewIsPrimary(e.target.checked)} className="rounded border-slate-300 text-emerald-600" />
                                    <span className="text-sm font-medium">Set as primary barcode</span>
                                </label>
                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button onClick={() => setShowAddModal(false)} className="px-5 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                    <button onClick={() => addMutation.mutate()} disabled={!newBarcode} className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" type="button">Add</button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
                            <Trash2 className="w-12 h-12 text-rose-500 mx-auto mb-3" /><h3 className="text-lg font-bold">Delete Barcode?</h3>
                            <div className="flex gap-3 mt-6"><button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                <button onClick={() => deleteMutation.mutate(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold">Delete</button></div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}